import React from "react";
import {
  Box,
  Typography,
  Chip,
  Paper,
  Stack,
  Divider,
  useTheme,
} from "@mui/material";
import { UnifiedInteractionLog } from "api/Community/CommunityInterfaces";

interface InteractionCardProps {
  log: UnifiedInteractionLog;
}

const pluralizeInteractionType = (interactionType: string) => {
  const normalized = interactionType
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");

  const routeMap: Record<string, string> = {
    "text-message": "text_messages",
    email: "emails",
    flyer: "flyers",
    "direct-mail": "direct_mails",
    conversation: "conversations",
    "door-knocking": "door_knockings",
    course: "courses",
    event: "events",
  };

  return routeMap[normalized] || `${normalized}s`;
};

const hasText = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

const InteractionCard: React.FC<InteractionCardProps> = ({ log }) => {
  const theme = useTheme();

  const interaction = log.related_interaction;
  const logDetails = log.log_details;

  /*
   * Only show the bordered log-details section if there is
   * actually something inside it that will be displayed.
   */
  const hasLogDetailsContent =
    !!logDetails &&
    (
      hasText(logDetails.notes) ||
      hasText(logDetails.subject_sent) ||
      hasText(logDetails.message_sent) ||
      hasText(logDetails.platform_used) ||
      (logDetails.session !== undefined &&
        logDetails.session !== null) ||
      hasText(logDetails.materials_provided)
    );

  const onClick = () => {
    if (!interaction?.id) return;

    const interactionTypePath = pluralizeInteractionType(
      log.interaction_type
    );

    window.open(
      `/interactions/${interactionTypePath}/${interaction.id}/`,
      "_blank"
    );
  };

  return (
    <Paper
      elevation={3}
      onClick={onClick}
      tabIndex={0}
      role="button"
      sx={{
        p: 2.5,
        borderRadius: 3,
        background: theme.custom.base.background.paper.dark,
        cursor: interaction?.id ? "pointer" : "default",
        transition: "0.15s ease",

        "&:hover": interaction?.id
          ? {
              boxShadow: 6,
              transform: "translateY(-2px)",
            }
          : undefined,

        "&:focus": {
          outline: `2px solid ${theme.custom.base.primary.dark}`,
          outlineOffset: "2px",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 1.5,
        }}
      >
        <Box sx={{ maxWidth: "70%" }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {interaction?.name || "Unnamed Interaction"}
          </Typography>

          {hasText(interaction?.description) && (
            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                color: "text.secondary",
                whiteSpace: "pre-wrap",
              }}
            >
              {interaction?.description}
            </Typography>
          )}
        </Box>

        <Box sx={{ textAlign: "right" }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600 }}
          >
            {log.interaction_type}
          </Typography>

          {typeof logDetails?.success === "boolean" && (
            <Chip
              label={
                logDetails.success
                  ? "Successful"
                  : "Unsuccessful"
              }
              size="small"
              color={
                logDetails.success
                  ? "success"
                  : "default"
              }
              variant="outlined"
              sx={{ mt: 0.7 }}
            />
          )}
        </Box>
      </Box>

      {/* Parent interaction notes */}
      {hasText(interaction?.notes) && (
        <Box
          sx={{
            border: `1px solid ${theme.custom.base.neutral[200].dark}`,
            p: 1.5,
            borderRadius: 2,
            mb: 2,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              mb: 0.5,
            }}
          >
            Notes for Team Members
          </Typography>

          <Typography
            variant="body2"
            sx={{
              whiteSpace: "pre-wrap",
              color:
                theme.custom.base.text.secondary.dark,
            }}
          >
            {interaction?.notes}
          </Typography>
        </Box>
      )}

      {/* Log details */}
      {hasLogDetailsContent && (
        <Box
          sx={{
            border: `1px solid ${theme.custom.base.neutral[200].dark}`,
            p: 1.5,
            borderRadius: 2,
            mb: 2,
          }}
        >
          <Stack spacing={1.5}>
            {/* Notes */}
            {hasText(logDetails?.notes) && (
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    mb: 0.3,
                  }}
                >
                  Notes
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-wrap",
                    color:
                      theme.custom.base.text.secondary.dark,
                  }}
                >
                  {logDetails.notes}
                </Typography>
              </Box>
            )}

            {/* Email subject */}
            {hasText(logDetails?.subject_sent) && (
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    mb: 0.3,
                  }}
                >
                  Subject
                </Typography>

                <Typography variant="body2">
                  {logDetails.subject_sent}
                </Typography>
              </Box>
            )}

            {/* Message sent */}
            {hasText(logDetails?.message_sent) && (
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    mb: 0.3,
                  }}
                >
                  Message
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {logDetails.message_sent}
                </Typography>
              </Box>
            )}

            {/* Platform */}
            {hasText(logDetails?.platform_used) && (
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    mb: 0.3,
                  }}
                >
                  Platform
                </Typography>

                <Typography variant="body2">
                  {logDetails.platform_used}
                </Typography>
              </Box>
            )}

            {/* Course session */}
            {logDetails?.session !== undefined &&
              logDetails?.session !== null && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                      mb: 0.3,
                    }}
                  >
                    Session
                  </Typography>

                  <Typography variant="body2">
                    {logDetails.session}
                  </Typography>
                </Box>
              )}

            {/* Materials */}
            {hasText(
              logDetails?.materials_provided
            ) && (
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    mb: 0.3,
                  }}
                >
                  Materials Provided
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {logDetails.materials_provided}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      )}

      <Divider sx={{ my: 1.5 }} />

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 2,
        }}
      >
        <Box>
          {hasText(interaction?.location) && (
            <Typography
              variant="caption"
              sx={{ display: "block" }}
            >
              📍 {interaction?.location}
            </Typography>
          )}
        </Box>

        <Box sx={{ textAlign: "right" }}>
          {logDetails?.timestamp && (
            <Typography
              variant="caption"
              color="text.secondary"
            >
              {new Date(
                logDetails.timestamp
              ).toLocaleString()}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default InteractionCard;